import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { BadgePreview } from "../components/BadgePreview";
import { BadgeBack } from "../components/BadgeBack";
import { ImportDialog } from "../components/ImportDialog";
import { MigrationDialog } from "../components/MigrationDialog";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";
import { loadSettings, getVerifyBaseUrl } from "../lib/settings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    Plus,
    Search,
    Pencil,
    Trash2,
    FileDown,
    Printer,
    Users,
    Image as ImageIcon,
    Loader2,
    FolderKanban,
    Lock,
    Unlock,
    KeyRound,
} from "lucide-react";
import { downloadA4BadgesPDF, printA4Badges } from "../lib/pdf";
import { recordPrintEvent } from "../lib/printHistory";
import { toast } from "sonner";

const FILTERS_KEY = "dipendenti_filters";
const fmtIt = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const todayIso = new Date().toISOString().slice(0, 10);
const readFilters = () => {
    try {
        return JSON.parse(sessionStorage.getItem(FILTERS_KEY)) || {};
    } catch {
        return {};
    }
};

export default function Dashboard() {
    const [employees, setEmployees] = useState([]);
    const [groups, setGroups] = useState([]);
    const [groupFilter, setGroupFilter] = useState(() => readFilters().group || "all"); // "all" or group.id
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(() => readFilters().search || "");
    const [statusFilter, setStatusFilter] = useState(() => readFilters().status || "attivi"); // attivi | cessati | tutti
    const [selected, setSelected] = useState(() => new Set());
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [printingA4, setPrintingA4] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [importOpen, setImportOpen] = useState(false);
    const [migrationOpen, setMigrationOpen] = useState(false);
    const [firmaDatore, setFirmaDatore] = useState(null);
    const [rotateBack, setRotateBack] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [lockedEmails, setLockedEmails] = useState(new Map()); // email -> {locked_until, count}
    const [unlockingId, setUnlockingId] = useState(null);
    // Single/multi selection prints default to WITHOUT client logo (toggle in the toolbar)
    const [includeClienteLogo, setIncludeClienteLogo] = useState(false);
    const [badgeFormat, setBadgeFormat] = useState("std"); // "std" 85,6×54 | "xl" 10×7 cm

    const printContainerRef = useRef(null);
    const verifyBase = getVerifyBaseUrl();

    const fetchSettings = async () => {
        const s = await loadSettings();
        setFirmaDatore(s.firma_datore || null);
        setRotateBack(!!s.rotate_back);
    };

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employees");
            setEmployees(data);
        } catch (e) {
            toast.error("Errore nel caricamento dipendenti");
        } finally {
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const { data } = await api.get("/groups");
            setGroups(data);
        } catch (e) {
            // Silently ignore — groups are optional
        }
    };

    const fetchLockedAccounts = async () => {
        try {
            const { data } = await api.get("/employees/locked-accounts");
            const map = new Map();
            (data?.locked || []).forEach((row) => {
                if (row?.email) map.set(row.email.toLowerCase(), row);
            });
            setLockedEmails(map);
        } catch (e) {
            // Silently ignore — non-blocking
        }
    };

    const handleUnlock = async (emp) => {
        if (!emp?.id) return;
        setUnlockingId(emp.id);
        try {
            await api.post(`/employees/${emp.id}/unlock-login`);
            toast.success(`Account di ${emp.cognome} ${emp.nome} sbloccato`);
            await fetchLockedAccounts();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Errore durante lo sblocco";
            toast.error(msg);
        } finally {
            setUnlockingId(null);
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchSettings();
        fetchGroups();
        fetchLockedAccounts();
        // Refresh locked-accounts list every 60s so a lockout that expires server-side
        // disappears from the UI without requiring a manual refresh.
        const interval = setInterval(fetchLockedAccounts, 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    // Listen for global settings updates (triggered by AppShell when SettingsDialog closes)
    useEffect(() => {
        const handler = () => fetchSettings();
        window.addEventListener("ss-settings-updated", handler);
        return () => window.removeEventListener("ss-settings-updated", handler);
    }, []);

    // Saved group may have been deleted in the meantime
    useEffect(() => {
        if (groups.length > 0 && groupFilter !== "all" && !groups.some((g) => g.id === groupFilter)) {
            setGroupFilter("all");
        }
    }, [groups, groupFilter]);

    const isCessato = (e) => !!(e.data_cessazione && String(e.data_cessazione).trim());
    const totalAttivi = useMemo(() => employees.filter((e) => !isCessato(e)).length, [employees]);
    const totalCessati = useMemo(() => employees.filter(isCessato).length, [employees]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let arr = employees;
        if (statusFilter === "attivi") arr = arr.filter((e) => !isCessato(e));
        else if (statusFilter === "cessati") arr = arr.filter(isCessato);
        if (groupFilter !== "all") {
            arr = arr.filter((e) => Array.isArray(e.group_ids) && e.group_ids.includes(groupFilter));
        }
        if (q) {
            arr = arr.filter(
                (e) =>
                    e.cognome.toLowerCase().includes(q) ||
                    e.nome.toLowerCase().includes(q) ||
                    e.matricola.toLowerCase().includes(q) ||
                    (e.qualifica || "").toLowerCase().includes(q)
            );
        }
        return arr;
    }, [employees, search, statusFilter, groupFilter]);

    // Sortable header — default order is the API order (sorted by cognome server-side).
    // `nome` column key sorts by cognome+nome combined so the header acts intuitively.
    const { sortedItems: filteredSorted, sortKey, sortDir, requestSort } = useSortableTable(filtered, {
        customAccessors: {
            nome: (e) => `${e.cognome} ${e.nome}`.toLowerCase(),
            cessazione: (e) => (isCessato(e) ? e.data_cessazione : e.contratto_scadenza) || null,
        },
        initialSort: { key: readFilters().sortKey || null, dir: readFilters().sortDir || "asc" },
    });

    // Remember filters + sort so "Torna ai dipendenti" restores the same view
    useEffect(() => {
        sessionStorage.setItem(
            FILTERS_KEY,
            JSON.stringify({ search, group: groupFilter, status: statusFilter, sortKey, sortDir })
        );
    }, [search, groupFilter, statusFilter, sortKey, sortDir]);

    const allChecked = filteredSorted.length > 0 && filteredSorted.every((e) => selected.has(e.id));
    const someChecked = filteredSorted.some((e) => selected.has(e.id)) && !allChecked;

    const toggleSelectAll = () => {
        const next = new Set(selected);
        if (allChecked) {
            filteredSorted.forEach((e) => next.delete(e.id));
        } else {
            filteredSorted.forEach((e) => next.add(e.id));
        }
        setSelected(next);
    };

    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/employees/${confirmDelete.id}`);
            toast.success("Dipendente eliminato");
            setEmployees((arr) => arr.filter((e) => e.id !== confirmDelete.id));
            const next = new Set(selected);
            next.delete(confirmDelete.id);
            setSelected(next);
        } catch (e) {
            toast.error("Errore durante l'eliminazione");
        } finally {
            setConfirmDelete(null);
        }
    };

    const selectedEmployees = useMemo(
        () => employees.filter((e) => selected.has(e.id)),
        [employees, selected]
    );

    // Map group id -> client logo, used to resolve the effective badge logo
    // (the group logo prevails over the personal one when the toggle is ON).
    const groupLogoById = useMemo(() => {
        const m = new Map();
        groups.forEach((g) => {
            if (g.cliente_logo) m.set(g.id, g.cliente_logo);
        });
        return m;
    }, [groups]);

    const badgeEmployeeFor = (emp) => ({
        ...emp,
        cliente_logo: includeClienteLogo
            ? ((emp.group_ids || []).map((gid) => groupLogoById.get(gid)).find(Boolean) || emp.cliente_logo || null)
            : null,
    });

    const handleExportA4 = async () => {
        if (selectedEmployees.length === 0) {
            toast.error("Seleziona almeno un dipendente");
            return;
        }
        setExporting(true);
        setProgress({ current: 0, total: selectedEmployees.length });
        await new Promise((r) => setTimeout(r, 150));
        try {
            const container = printContainerRef.current;
            if (!container) throw new Error("container missing");
            const items = Array.from(container.querySelectorAll("[data-print-pair]")).map((wrap) => ({
                front: wrap.querySelector("[data-print-front]"),
                back: wrap.querySelector("[data-print-back]"),
            }));
            await downloadA4BadgesPDF(items, "splendor-sud-tesserini-a4.pdf", {
                includeBack: true,
                rotateBack,
                format: badgeFormat,
                onProgress: (p) => setProgress(p),
            });
            const ids = selectedEmployees.map((e) => e.id);
            recordPrintEvent("a4_pdf", ids, items.length);
            toast.success(`Esportati ${items.length} tesserini (fronte + retro)`);
        } catch (e) {
            toast.error("Errore durante l'esportazione PDF");
        } finally {
            setExporting(false);
            setProgress({ current: 0, total: 0 });
        }
    };

    const handlePrintA4 = async () => {
        if (selectedEmployees.length === 0) {
            toast.error("Seleziona almeno un dipendente");
            return;
        }
        setPrintingA4(true);
        setProgress({ current: 0, total: selectedEmployees.length });
        await new Promise((r) => setTimeout(r, 150));
        try {
            const container = printContainerRef.current;
            if (!container) throw new Error("container missing");
            const items = Array.from(container.querySelectorAll("[data-print-pair]")).map((wrap) => ({
                front: wrap.querySelector("[data-print-front]"),
                back: wrap.querySelector("[data-print-back]"),
            }));
            await printA4Badges(items, { includeBack: true, rotateBack, format: badgeFormat, onProgress: (p) => setProgress(p) });
            const ids = selectedEmployees.map((e) => e.id);
            recordPrintEvent("a4_print", ids, items.length);
            toast.success("Anteprima di stampa aperta");
        } catch (e) {
            toast.error("Errore durante la stampa");
        } finally {
            setPrintingA4(false);
            setProgress({ current: 0, total: 0 });
        }
    };

    // Action shortcuts from the sidebar (Tesserini → Stampa/Esporta A4, Personale → Migrazione/Importa Excel)
    // arrive as `?action=...` URL params. We open the corresponding dialog or trigger the action,
    // then clear the param so the user can re-fire it later without reloading the page.
    useEffect(() => {
        const action = searchParams.get("action");
        if (!action) return;
        if (action === "migration") {
            setMigrationOpen(true);
        } else if (action === "import") {
            setImportOpen(true);
        } else if (action === "print-a4") {
            handlePrintA4();
        } else if (action === "export-a4") {
            handleExportA4();
        }
        setSearchParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const total = employees.length;

    return (
        <div data-testid="dashboard-page">
            <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                {/* Heading */}
                <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                            Gestionale
                        </div>
                        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#376DB3] sm:text-4xl">
                            Dipendenti
                        </h1>
                        <p className="mt-1 text-sm text-slate-600">
                            Anagrafiche e generazione tesserini identificativi.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link to="/dipendenti/nuovo">
                            <Button
                                className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="add-employee-button"
                            >
                                <Plus className="h-4 w-4" />
                                Nuovo Dipendente
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Inline progress banner — shown while a sidebar-triggered A4 action is running */}
                {(printingA4 || exporting) && (
                    <div
                        className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-[#5BAEDB]/40 bg-[#E8F1FA] px-4 py-3 text-sm shadow-sm"
                        data-testid="a4-progress-banner"
                    >
                        <div className="flex items-center gap-3">
                            <Loader2 className="h-4 w-4 animate-spin text-[#376DB3]" />
                            <div>
                                <div className="font-semibold text-[#15498F]">
                                    {printingA4 ? "Preparo la stampa A4…" : "Esporto il PDF A4…"}
                                </div>
                                {progress.total > 0 && (
                                    <div className="text-xs text-slate-600">
                                        Tesserino {progress.current} di {progress.total}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats */}
                <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard
                        label="Totale dipendenti"
                        value={total}
                        icon={<Users className="h-4 w-4" />}
                        testId="stat-total"
                    />
                    <StatCard
                        label="Attivi"
                        value={totalAttivi}
                        icon={<Users className="h-4 w-4" />}
                        testId="stat-attivi"
                        tone="active"
                    />
                    <StatCard
                        label="Cessati"
                        value={totalCessati}
                        icon={<Users className="h-4 w-4" />}
                        testId="stat-cessati"
                        tone="cessati"
                    />
                    <StatCard
                        label="Selezionati"
                        value={selected.size}
                        icon={<FileDown className="h-4 w-4" />}
                        testId="stat-selected"
                    />
                </div>

                {/* Status filter + Group filter + Search */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                        <Tabs value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setSelected(new Set()); }}>
                            <TabsList data-testid="status-filter-tabs">
                                <TabsTrigger value="attivi" data-testid="tab-attivi">Attivi ({totalAttivi})</TabsTrigger>
                                <TabsTrigger value="cessati" data-testid="tab-cessati">Cessati ({totalCessati})</TabsTrigger>
                                <TabsTrigger value="tutti" data-testid="tab-tutti">Tutti ({total})</TabsTrigger>
                            </TabsList>
                        </Tabs>
                        <div className={`flex items-center gap-2 ${groups.length > 0 ? "" : "hidden"}`}>
                            <FolderKanban className="h-4 w-4 text-slate-400" />
                            <Select
                                value={groupFilter}
                                onValueChange={(v) => { setGroupFilter(v); setSelected(new Set()); }}
                            >
                                <SelectTrigger className="h-9 w-[220px]" data-testid="group-filter-select">
                                    <SelectValue placeholder="Tutti i gruppi" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i gruppi</SelectItem>
                                    {groups.map((g) => (
                                        <SelectItem key={g.id} value={g.id}>
                                            {g.name} ({g.member_count})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div
                            className={`flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 ${selected.size > 0 ? "" : "hidden"}`}
                            data-testid="dashboard-cliente-logo-toggle-row"
                            title="Se attivo, i tesserini selezionati includono il logo cliente (quello del gruppo prevale sul personale)"
                        >
                            <Switch
                                checked={includeClienteLogo}
                                onCheckedChange={setIncludeClienteLogo}
                                data-testid="dashboard-toggle-cliente-logo"
                            />
                            <span className="text-xs text-slate-600">Includi logo cliente</span>
                        </div>
                        <div
                            className={`flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5 ${selected.size > 0 ? "" : "hidden"}`}
                            title="Formato dei tesserini per stampa/export A4"
                            data-testid="dashboard-format-toggle-row"
                        >
                            <button
                                type="button"
                                onClick={() => setBadgeFormat("std")}
                                className={`rounded px-2 py-1 text-[11px] font-mono-numbers transition ${badgeFormat === "std" ? "bg-[#376DB3] text-white" : "text-slate-500 hover:bg-slate-100"}`}
                                data-testid="dashboard-format-std"
                            >
                                85,6×54
                            </button>
                            <button
                                type="button"
                                onClick={() => setBadgeFormat("xl")}
                                className={`rounded px-2 py-1 text-[11px] font-mono-numbers transition ${badgeFormat === "xl" ? "bg-[#376DB3] text-white" : "text-slate-500 hover:bg-slate-100"}`}
                                data-testid="dashboard-format-xl"
                            >
                                10×7 cm
                            </button>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handlePrintA4}
                            disabled={printingA4 || exporting}
                            className={`gap-1.5 ${selected.size > 0 ? "" : "hidden"}`}
                            data-testid="print-a4-page-btn"
                        >
                            {printingA4 ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                            Stampa A4
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleExportA4}
                            disabled={printingA4 || exporting}
                            className={`gap-1.5 bg-[#376DB3] text-white hover:bg-[#376DB3]/90 ${selected.size > 0 ? "" : "hidden"}`}
                            data-testid="export-a4-page-btn"
                        >
                            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                            Esporta A4
                        </Button>
                    </div>
                    <div className="relative flex-1 sm:max-w-md">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Cerca per nome, cognome, matricola, qualifica…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                            data-testid="search-input"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={allChecked || (someChecked ? "indeterminate" : false)}
                                        onCheckedChange={toggleSelectAll}
                                        data-testid="select-all-checkbox"
                                        aria-label="Seleziona tutti"
                                    />
                                </TableHead>
                                <SortableHead sortKey="matricola" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Matricola
                                </SortableHead>
                                <TableHead className="w-16">Foto</TableHead>
                                <SortableHead sortKey="nome" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Nome
                                </SortableHead>
                                <SortableHead sortKey="qualifica" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden md:table-cell">
                                    Qualifica
                                </SortableHead>
                                <SortableHead sortKey="data_assunzione" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden lg:table-cell">
                                    Assunzione
                                </SortableHead>
                                <SortableHead sortKey="cessazione" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden lg:table-cell">
                                    Cessazione
                                </SortableHead>
                                <TableHead className="text-right">Azioni</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="py-16 text-center">
                                        <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
                                    </TableCell>
                                </TableRow>
                            ) : filteredSorted.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="py-16 text-center">
                                        <div className="mx-auto max-w-sm">
                                            <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                                            <div className="font-display text-base font-semibold text-slate-700">
                                                {search ? "Nessun risultato" : "Nessun dipendente"}
                                            </div>
                                            <p className="mt-1 text-sm text-slate-500">
                                                {search
                                                    ? "Prova a modificare la ricerca."
                                                    : "Aggiungi il primo dipendente per generare il suo tesserino."}
                                            </p>
                                            {!search && (
                                                <Link to="/dipendenti/nuovo" className="mt-4 inline-block">
                                                    <Button
                                                        className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                                        data-testid="empty-add-button"
                                                    >
                                                        <Plus className="mr-2 h-4 w-4" />
                                                        Aggiungi dipendente
                                                    </Button>
                                                </Link>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredSorted.map((emp) => (
                                    <TableRow
                                        key={emp.id}
                                        data-testid={`employee-row-${emp.id}`}
                                        className="hover:bg-slate-50/60"
                                    >
                                        <TableCell>
                                            <Checkbox
                                                checked={selected.has(emp.id)}
                                                onCheckedChange={() => toggleOne(emp.id)}
                                                data-testid={`row-checkbox-${emp.id}`}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="font-mono-numbers">
                                                {emp.matricola}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {emp.photo ? (
                                                <img
                                                    src={emp.photo}
                                                    alt=""
                                                    className="h-10 w-10 rounded-md object-cover ring-1 ring-slate-200"
                                                />
                                            ) : (
                                                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-400">
                                                    <ImageIcon className="h-4 w-4" />
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Link
                                                to={`/dipendenti/${emp.id}`}
                                                className="flex items-center gap-2 hover:underline"
                                                data-testid={`view-${emp.id}`}
                                            >
                                                <div className="font-medium text-[#376DB3]">
                                                    {emp.cognome} {emp.nome}
                                                </div>
                                                {isCessato(emp) && (
                                                    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px] uppercase tracking-wide" data-testid={`badge-cessato-${emp.id}`}>
                                                        Cessato
                                                    </Badge>
                                                )}
                                                {isCessato(emp) && emp.can_login && emp.portal_access && (
                                                    <Badge
                                                        variant="outline"
                                                        className={`gap-1 whitespace-nowrap text-[10px] uppercase tracking-wide ${emp.portal_access.status === "blocked" ? "border-red-300 bg-red-50 text-red-700" : "border-sky-300 bg-sky-50 text-sky-700"}`}
                                                        title={emp.portal_access.status === "blocked"
                                                            ? `Accesso al portale bloccato: consentito fino al ${fmtIt(emp.portal_access.until)}${emp.portal_access.override ? " (data impostata dall'ufficio)" : " (regola: 31/03 dell'anno dopo la cessazione)"}`
                                                            : `Il dipendente cessato può ancora accedere al portale fino al ${fmtIt(emp.portal_access.until)}${emp.portal_access.override ? " (data impostata dall'ufficio)" : " (regola: 31/03 dell'anno dopo la cessazione)"}`}
                                                        data-testid={`badge-portal-access-${emp.id}`}
                                                    >
                                                        {emp.portal_access.status === "blocked" ? <Lock className="h-2.5 w-2.5" /> : <KeyRound className="h-2.5 w-2.5" />}
                                                        {emp.portal_access.status === "blocked" ? "Accesso bloccato" : `Accesso fino al ${fmtIt(emp.portal_access.until)}`}
                                                    </Badge>
                                                )}
                                                {emp.email && lockedEmails.has(emp.email.toLowerCase()) && (
                                                    <Badge
                                                        variant="outline"
                                                        className="gap-1 border-red-300 bg-red-50 text-red-700 text-[10px] uppercase tracking-wide"
                                                        title={`Account bloccato fino a ${
                                                            (() => {
                                                                try {
                                                                    const d = new Date(lockedEmails.get(emp.email.toLowerCase())?.locked_until);
                                                                    return d.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
                                                                } catch { return ""; }
                                                            })()
                                                        } per troppi tentativi falliti`}
                                                        data-testid={`badge-locked-${emp.id}`}
                                                    >
                                                        <Lock className="h-2.5 w-2.5" />
                                                        Bloccato
                                                    </Badge>
                                                )}
                                            </Link>
                                            <div className="text-xs text-slate-500 md:hidden">
                                                {emp.qualifica}
                                            </div>
                                        </TableCell>
                                        <TableCell className="hidden text-slate-700 md:table-cell">
                                            {emp.qualifica}
                                        </TableCell>
                                        <TableCell className="hidden text-slate-600 lg:table-cell font-mono-numbers">
                                            {emp.data_assunzione
                                                ? emp.data_assunzione.split("-").reverse().join("/")
                                                : "—"}
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell" data-testid={`cessazione-${emp.id}`}>
                                            {isCessato(emp) ? (
                                                <div>
                                                    <div className="font-mono-numbers text-slate-700">{fmtIt(emp.data_cessazione)}</div>
                                                    <div className="text-[10px] uppercase tracking-wide text-amber-700">effettiva</div>
                                                </div>
                                            ) : emp.contratto_scadenza ? (
                                                <div>
                                                    <div className={`font-mono-numbers ${emp.contratto_scadenza < todayIso ? "text-red-600" : "text-slate-700"}`}>
                                                        {fmtIt(emp.contratto_scadenza)}
                                                    </div>
                                                    <div className="text-[10px] uppercase tracking-wide text-slate-400">
                                                        {emp.contratto_scadenza < todayIso ? "prevista · scaduta" : "prevista (TD)"}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                {emp.email && lockedEmails.has(emp.email.toLowerCase()) && (
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        title="Sblocca account (rimuovi blocco anti-bruteforce)"
                                                        onClick={() => handleUnlock(emp)}
                                                        disabled={unlockingId === emp.id}
                                                        data-testid={`unlock-${emp.id}`}
                                                        className="text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                                                    >
                                                        {unlockingId === emp.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Unlock className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                )}
                                                <Link to={`/dipendenti/${emp.id}/modifica`}>
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        title="Modifica"
                                                        data-testid={`edit-${emp.id}`}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                </Link>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    title="Elimina"
                                                    onClick={() => setConfirmDelete(emp)}
                                                    data-testid={`delete-${emp.id}`}
                                                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Hidden print container for A4 export — rendered offscreen */}
                <div className="print-only" aria-hidden ref={printContainerRef}>
                    {selectedEmployees.map((emp) => (
                        <div key={emp.id} data-print-pair>
                            <div data-print-front>
                                <BadgePreview employee={badgeEmployeeFor(emp)} size={badgeFormat} />
                            </div>
                            <div data-print-back>
                                <BadgeBack employee={emp} firmaDatore={firmaDatore} verifyBaseUrl={verifyBase} size={badgeFormat} />
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            <ImportDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                onImported={fetchEmployees}
            />
            <MigrationDialog
                open={migrationOpen}
                onOpenChange={setMigrationOpen}
                onImported={fetchEmployees}
            />

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent data-testid="confirm-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare il dipendente?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmDelete &&
                                `${confirmDelete.cognome} ${confirmDelete.nome} (matricola ${confirmDelete.matricola}) verrà eliminato definitivamente.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-delete-cancel">Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete-confirm"
                        >
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

const StatCard = ({ label, value, icon, testId, tone }) => {
    const colorByTone = {
        active: "text-emerald-600",
        cessati: "text-amber-600",
    };
    const valueColor = colorByTone[tone] || "text-[#376DB3]";
    const iconBg = tone === "active"
        ? "bg-emerald-50 text-emerald-600"
        : tone === "cessati"
        ? "bg-amber-50 text-amber-600"
        : "bg-slate-100 text-[#376DB3]";
    return (
        <div
            className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
            data-testid={testId}
        >
            <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {label}
                </div>
                <div className={`font-display text-2xl font-bold font-mono-numbers ${valueColor}`}>
                    {value}
                </div>
            </div>
            <div className={`flex h-9 w-9 items-center justify-center rounded-md ${iconBg}`}>
                {icon}
            </div>
        </div>
    );
};
